import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Award,
  Calendar,
  Code,
  Palette,
  Briefcase,
  MessageCircle,
  Clock,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import AdminDashboard from "./admin/AdminDashboard";
import { apiService } from "../services/api";
import ProgressChart from "../components/ProgressChart";
import styles from "./Dashboard.module.css";

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("Overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [recommendedAssessments, setRecommendedAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await apiService.progress.getDashboard();
        setDashboardData(data);

        // Fetch recommended assessments
        const recommendedData =
          await apiService.progress.getRecommendedAssessments(4);
        setRecommendedAssessments(recommendedData?.recommendations || []);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // If user is admin, show admin dashboard
  if (isAdmin()) {
    return <AdminDashboard />;
  }

  if (loading) {
    return (
      <div className={styles.dashboardPage}>
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  const stats = {
    assessmentsCompleted: dashboardData?.assessmentStats?.totalAttempts || 0,
    skillsInProgress: dashboardData?.enrolledPaths?.length || 0,
    learningStreak: dashboardData?.learningStreak || 0,
  };

  // Calculate top skills from assessment history
  const calculateTopSkills = () => {
    // If backend provides topSkills, use that
    if (dashboardData?.topSkills && dashboardData.topSkills.length > 0) {
      const getSkillLevel = (avgScore) => {
        if (avgScore >= 90) return "Expert";
        if (avgScore >= 75) return "Advanced";
        if (avgScore >= 60) return "Intermediate";
        return "Beginner";
      };

      const getSkillColor = (avgScore) => {
        if (avgScore >= 90) return "#4CAF50"; // Green
        if (avgScore >= 75) return "#2196F3"; // Blue
        if (avgScore >= 60) return "#FF9800"; // Orange
        return "#9C27B0"; // Purple
      };

      return dashboardData.topSkills.slice(0, 4).map((skill) => ({
        name: skill.skillName,
        progress: skill.averageScore,
        level: getSkillLevel(skill.averageScore),
        color: getSkillColor(skill.averageScore),
        assessmentCount: skill.assessmentCount,
      }));
    }

    // Fallback: calculate from recent assessments if topSkills not provided
    if (
      !dashboardData?.recentAssessments ||
      dashboardData.recentAssessments.length === 0
    ) {
      return [];
    }

    // Aggregate skills from all assessments
    const skillMap = {};

    dashboardData.recentAssessments.forEach((assessment) => {
      const skillName = assessment.skillName || "General";
      const percentage = assessment.percentage || 0;

      if (!skillMap[skillName]) {
        skillMap[skillName] = {
          name: skillName,
          scores: [],
          totalScore: 0,
          count: 0,
        };
      }

      skillMap[skillName].scores.push(percentage);
      skillMap[skillName].totalScore += percentage;
      skillMap[skillName].count += 1;
    });

    // Calculate average and determine level
    const getSkillLevel = (avgScore) => {
      if (avgScore >= 90) return "Expert";
      if (avgScore >= 75) return "Advanced";
      if (avgScore >= 60) return "Intermediate";
      return "Beginner";
    };

    const getSkillColor = (avgScore) => {
      if (avgScore >= 90) return "#4CAF50"; // Green
      if (avgScore >= 75) return "#2196F3"; // Blue
      if (avgScore >= 60) return "#FF9800"; // Orange
      return "#9C27B0"; // Purple
    };

    // Convert to array and calculate averages
    const skills = Object.values(skillMap).map((skill) => ({
      name: skill.name,
      progress: Math.round(skill.totalScore / skill.count),
      level: getSkillLevel(skill.totalScore / skill.count),
      color: getSkillColor(skill.totalScore / skill.count),
      assessmentCount: skill.count,
    }));

    // Sort by progress (highest first) and return top 4
    return skills.sort((a, b) => b.progress - a.progress).slice(0, 4);
  };

  const topSkills = calculateTopSkills();

  const recentAssessments = dashboardData?.recentAssessments || [];

  // Calculate dynamic skill radar data from topSkills
  const skillRadarData =
    dashboardData?.topSkills && dashboardData.topSkills.length > 0
      ? dashboardData.topSkills.slice(0, 6).map((skill) => ({
          skill: skill.skillName,
          value: skill.averageScore,
        }))
      : topSkills.slice(0, 6).map((skill) => ({
          skill: skill.name,
          value: skill.progress,
        }));

  // Map icon names to actual icon components
  const getIconComponent = (iconName) => {
    const iconMap = {
      Code,
      Palette,
      Briefcase,
      MessageCircle,
      BookOpen,
    };
    return iconMap[iconName] || BookOpen;
  };

  return (
    <div className={styles.dashboardPage}>
      <div className={styles.dashboardHeader}>
        <div className={styles.headerTabs}>
          {[""].map((tab) => (
            <button
              key={tab}
              className={`${styles.tabButton} ${activeTab === tab ? styles.active : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.dashboardContent}>
        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.blue}`}>
            <div className={styles.statIcon}>
              <Award size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {stats.assessmentsCompleted}
              </div>
              <div className={styles.statLabel}>Assessments Completed</div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.purple}`}>
            <div className={styles.statIcon}>
              <BookOpen size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.skillsInProgress}</div>
              <div className={styles.statLabel}>Skills in Progress</div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.orange}`}>
            <div className={styles.statIcon}>
              <Calendar size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {stats.learningStreak}{" "}
                {stats.learningStreak === 1 ? "day" : "days"}
              </div>
              <div className={styles.statLabel}>Learning Streak</div>
              <div className={styles.statMeta}>
                {stats.learningStreak === 0
                  ? "Start your streak today!"
                  : stats.learningStreak === 1
                    ? "Great start!"
                    : stats.learningStreak < 7
                      ? "Keep it going!"
                      : stats.learningStreak < 30
                        ? "Amazing streak! 🔥"
                        : "Incredible dedication! 🏆"}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className={styles.contentGrid}>
          {/* Skill Progress Overview - Using Recharts */}
          <div className={`${styles.contentCard} ${styles.skillOverview}`}>
            <ProgressChart
              skillData={skillRadarData.map((item) => ({
                skill: item.skill,
                proficiency: item.value,
              }))}
              trendData={recentAssessments.map((a) => ({
                date: a.completedAt || new Date().toISOString(),
                score: a.percentage || 0,
              }))}
              recentAssessments={recentAssessments.map((a) => ({
                name: a.title,
                score: a.percentage || 0,
                passing_score: 70,
              }))}
              title="Skill Progress Overview"
            />
          </div>

          {/* Top Skills */}
          <div className={styles.contentCard}>
            <div className={styles.cardHeader}>
              <h3>Top Skills</h3>
              <Link to="/progress" className={styles.viewAll}>
                View all
              </Link>
            </div>
            <div className={styles.skillsList}>
              {topSkills.length > 0 ? (
                topSkills.map((skill, index) => (
                  <div key={index} className={styles.skillItem}>
                    <div className={styles.skillInfo}>
                      <div className={styles.skillName}>{skill.name}</div>
                      <div className={styles.skillLevel}>{skill.level}</div>
                    </div>
                    <div className={styles.skillProgress}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${skill.progress}%`,
                            backgroundColor: skill.color,
                          }}
                        ></div>
                      </div>
                      <span className={styles.progressText}>
                        {skill.progress}%
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noSkills}>
                  <p>
                    No skills data yet. Complete assessments to see your top
                    skills!
                  </p>
                  <Link to="/assessments" className="btn btn-primary">
                    Take an Assessment
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Assessments */}
          <div className={styles.contentCard}>
            <div className={styles.cardHeader}>
              <h3>Recent Assessments</h3>
              <Link to="/assessments?tab=history" className={styles.viewAll}>
                View all assessments
              </Link>
            </div>
            <div className={styles.assessmentsList}>
              {recentAssessments.length > 0 ? (
                recentAssessments.map((assessment, index) => (
                  <div key={index} className={styles.assessmentItem}>
                    <div className={styles.assessmentInfo}>
                      <div className={styles.assessmentName}>
                        {assessment.title}
                      </div>
                      <div className={styles.assessmentSkill}>
                        {assessment.skillName && (
                          <span className={styles.skillBadge}>
                            {assessment.skillName}
                          </span>
                        )}
                      </div>
                      <div className={styles.assessmentScore}>
                        {assessment.status === "completed" ? (
                          <span className={styles.score}>
                            {assessment.percentage}%
                          </span>
                        ) : (
                          <span className={styles.pending}>Failed</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.assessmentActions}>
                      {assessment.status === "completed" ? (
                        <div className={styles.completedActions}>
                          <div className={styles.statusCompleted}>✓</div>
                          <Link
                            to={`/assessment-report/${assessment.id}`}
                            className={styles.viewReportLink}
                          >
                            View Details
                          </Link>
                        </div>
                      ) : (
                        <div className={styles.assessmentActions}>
                          <div className={styles.statusPending}>❌</div>
                          <Link
                            to={`/assessment-report/${assessment.id}`}
                            className={styles.viewReportLink}
                          >
                            View Details
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noAssessments}>
                  <p>No recent assessments found.</p>
                  <Link to="/assessments" className="btn btn-primary">
                    Take Your First Assessment
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recommended Assessments Section */}
        <div
          className={`${styles.contentCard} ${styles.recommendedAssessments}`}
        >
          <div className={styles.cardHeader}>
            <h3>Recommended Assessments</h3>
            <Link to="/assessments" className={styles.viewAll}>
              View all assessments
            </Link>
          </div>
          <div className={styles.recommendedGrid}>
            {recommendedAssessments.length > 0 ? (
              recommendedAssessments.map((assessment, index) => {
                const IconComponent = getIconComponent(assessment.icon);
                return (
                  <div
                    key={assessment.id || index}
                    className={styles.recommendedItem}
                  >
                    <div className={styles.recommendedHeader}>
                      <div
                        className={styles.recommendedIcon}
                        style={{
                          backgroundColor: `${assessment.color}20`,
                          color: assessment.color,
                        }}
                      >
                        <IconComponent size={20} />
                      </div>
                      <div className={styles.recommendedMeta}>
                        <span className={styles.recommendedCategory}>
                          {assessment.category}
                        </span>
                        <div className={styles.recommendedDetails}>
                          <span className={styles.difficulty}>
                            {assessment.difficulty}
                          </span>
                          <span className={styles.duration}>
                            <Clock size={12} />
                            {assessment.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.recommendedContent}>
                      <h4 className={styles.recommendedTitle}>
                        {assessment.name}
                      </h4>
                      <p className={styles.recommendedDescription}>
                        {assessment.description}
                      </p>
                      {assessment.reason && (
                        <p className={styles.recommendedReason}>
                          💡 {assessment.reason}
                        </p>
                      )}
                    </div>
                    <div className={styles.recommendedActions}>
                      <Link
                        to={`/assessment/${assessment.id}`}
                        className={styles.btnStartAssessment}
                      >
                        Start Assessment
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.noRecommendations}>
                <p>No recommendations available at the moment.</p>
                <Link to="/assessments" className="btn btn-primary">
                  Browse All Assessments
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
